import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: ${props => props.$isClosing ? slideOut : slideIn} 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const NotificationIcon = styled.div`
  font-size: 28px;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
  color: white;
`;

const NotificationMessage = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 16px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const InAppNotification = () => {
  const [notification, setNotification] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Показ уведомления
  const showNotification = (title, message) => {
    if (visible) return;
    
    setNotification({ title, message });
    setVisible(true);
    setIsClosing(false);
    
    // Автоскрытие через 6 секунд
    setTimeout(() => {
      setIsClosing(true);
      setTimeout(() => {
        setVisible(false);
        setNotification(null);
      }, 300);
    }, 6000);
  };

  // Сохраняем функцию глобально
  useEffect(() => {
    window.showInAppNotification = showNotification;
    
    // Проверка ближайших записей при загрузке
    checkUpcomingBookings();
    
    // Проверка каждые 30 минут
    const interval = setInterval(checkUpcomingBookings, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Проверка ближайших записей
  const checkUpcomingBookings = () => {
    try {
      const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      const now = new Date();
      const shownReminders = JSON.parse(localStorage.getItem('shownReminders') || '[]');
      
      bookings.forEach(booking => {
        const bookingDate = new Date(booking.date);
        const hoursLeft = (bookingDate - now) / (1000 * 60 * 60);
        
        // За 24 часа до тренировки
        if (hoursLeft <= 24 && hoursLeft > 23 && !shownReminders.includes(booking.id)) {
          showNotification(
            '🔔 Напоминание о тренировке',
            `Завтра в ${booking.timeSlots[0]} у вас теннис на корте №${booking.court === 'court1' ? '1' : '2'}`
          );
          
          shownReminders.push(booking.id);
          localStorage.setItem('shownReminders', JSON.stringify(shownReminders));
        }
        
        // За 2 часа до тренировки
        if (hoursLeft <= 2 && hoursLeft > 1 && !shownReminders.includes(`${booking.id}_2h`)) {
          showNotification(
            '🎾 Сегодня тренировка!',
            `Через 2 часа теннис на корте №${booking.court === 'court1' ? '1' : '2'}. Ждем вас!`
          );
          
          shownReminders.push(`${booking.id}_2h`);
          localStorage.setItem('shownReminders', JSON.stringify(shownReminders));
        }
      });
    } catch (error) {
      console.error('Ошибка проверки записей:', error);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setVisible(false);
      setNotification(null);
    }, 300);
  };

  if (!visible || !notification) return null;

  return (
    <NotificationContainer $isClosing={isClosing}>
      <NotificationIcon>🎾</NotificationIcon>
      <NotificationContent>
        <NotificationTitle>{notification.title}</NotificationTitle>
        <NotificationMessage>{notification.message}</NotificationMessage>
      </NotificationContent>
      <CloseButton onClick={handleClose}>✕</CloseButton>
    </NotificationContainer>
  );
};

export default InAppNotification;